import { useMemo } from "react";
import { RowData } from "@tanstack/react-table";
import type { ViewTableInstance } from "@design-system/ViewTable/core/table";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";

interface AccrualAwareTableMetaResult<TCallbacks> {
    callbacks?: TCallbacks;
    accrualEntityIds: Set<number>;
    isLoadingAccrualLedgers: boolean;
}

/**
 * Shared accessor for views that branch open-amount rendering on whether
 * the row's entity has an accrual ledger. Wraps `useViewTableMeta` so each
 * view does not need its own `getXTableMeta` helper, and defaults
 * `accrualEntityIds` to an empty `Set` while the ledger query is in flight.
 */
export function useAccrualAwareTableMeta<TCallbacks, TData extends RowData>(
    table: ViewTableInstance<TData>,
): AccrualAwareTableMetaResult<TCallbacks> {
    const { callbacks } = useViewTableMeta<TCallbacks, unknown>(table);
    const accrualEntityIds = table.options.meta?.accrualEntityIds;
    const isLoadingAccrualLedgers =
        table.options.meta?.isLoadingAccrualLedgers ?? false;

    return useMemo(
        () => ({
            callbacks,
            accrualEntityIds: accrualEntityIds ?? new Set<number>(),
            isLoadingAccrualLedgers,
        }),
        [callbacks, accrualEntityIds, isLoadingAccrualLedgers],
    );
}
